import { useMutation } from "@apollo/client";
import { XIcon } from "@heroicons/react/outline";
import undiscussedTopicSvg from "@static/img/topic-undiscussed.svg";
import { compact, toInteger, uniqBy } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import store from "store2";
import {
  AddPreviousMeetingTopicsToCurrentMeetingMutationMutation,
  AddPreviousMeetingTopicsToCurrentMeetingMutationMutationVariables,
  MarkPreviousMeetingTopicsAsDiscussedMutationMutation,
  MarkPreviousMeetingTopicsAsDiscussedMutationMutationVariables,
  MeetingViewMeetingNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import addPreviousMeetingTopicsMutation from "@apps/meeting-new/graphql/add-previous-meeting-topics-mutation";
import markPreviousMeetingTopicsDiscussedMutation from "@apps/meeting-new/graphql/mark-previous-meeting-topics-discussed-mutation";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { getAssetUrl } from "@helpers/helpers";
import { pluralize } from "@helpers/string";

const storageKey = "meeting-dismissed-undiscussed-topics";
const howManyDaysAreTheMeetingNotesPersisted = 7;

const UndiscussedTopicsBanner = ({
  meeting,
  onTopicsChanged,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  onTopicsChanged?: () => void;
}) => {
  const label = useLabel();
  const previousMeeting = meeting.previousMeeting;
  const dismissedMeetings: { id: number; time: number }[] =
    store(storageKey) || [];

  const isPreviousMeetingDismissed =
    previousMeeting &&
    dismissedMeetings.some(
      (dismissedMeeting) => dismissedMeeting.id === previousMeeting.id,
    );
  const [dismissed, setDismissed] = useState(isPreviousMeetingDismissed);

  useEffect(() => {
    setDismissed(Boolean(isPreviousMeetingDismissed));
  }, [isPreviousMeetingDismissed, previousMeeting?.id]);

  const handleDismiss = () => {
    if (!previousMeeting) return;

    // remove old dismissed meetings to keep the storage somewhat cleaned.
    const pastDateThreshold =
      Date.now() - howManyDaysAreTheMeetingNotesPersisted * 24 * 60 * 60 * 1000;
    const updateDismissedMeetings = uniqBy(
      [
        ...dismissedMeetings,
        { id: previousMeeting.id, time: toInteger(Date.now() / 1000) },
      ],
      "id",
    ).filter(
      (dismissedMeeting) => dismissedMeeting.time * 1000 > pastDateThreshold,
    );
    store(storageKey, updateDismissedMeetings);
    setDismissed(true);
  };

  const [addTopics, { loading: addLoading }] = useMutation<
    AddPreviousMeetingTopicsToCurrentMeetingMutationMutation,
    AddPreviousMeetingTopicsToCurrentMeetingMutationMutationVariables
  >(addPreviousMeetingTopicsMutation);

  const [markDiscussed, { loading: markLoading }] = useMutation<
    MarkPreviousMeetingTopicsAsDiscussedMutationMutation,
    MarkPreviousMeetingTopicsAsDiscussedMutationMutationVariables
  >(markPreviousMeetingTopicsDiscussedMutation);

  const undiscussedTopics = compact(
    meeting.undiscussedPreviousMeetingTopics ?? [],
  );

  const previousMeetingIsCompleted = moment().isAfter(
    moment(meeting.previousMeeting?.startDatetime),
    "minute",
  );

  const meetingIsTooFarInThePast = moment(meeting.startDatetime).isBefore(
    moment().subtract(howManyDaysAreTheMeetingNotesPersisted, "day"),
    "day",
  );
  if (
    meetingIsTooFarInThePast ||
    !previousMeeting ||
    dismissed ||
    undiscussedTopics.length === 0 ||
    !previousMeetingIsCompleted
  ) {
    return null;
  }

  const topicCount = undiscussedTopics.length;
  const isLoading = addLoading || markLoading;

  const handleAddAll = () => {
    addTopics({
      variables: { meetingId: meeting.id },
      onCompleted: () => {
        successNotificationVar({
          title: "Topics from the last meeting were added.",
          timeout: 3000,
        });
        handleDismiss();
        onTopicsChanged?.();
      },
    });
  };

  const handleMarkAllDiscussed = () => {
    markDiscussed({
      variables: { meetingId: meeting.id },
      onCompleted: () => {
        successNotificationVar({
          title: "Topics from the last meeting were marked as discussed.",
          timeout: 3000,
        });
        handleDismiss();
      },
    });
  };

  return (
    <div className="flex gap-3 items-start bg-gray-100 p-4 rounded-md">
      <TopicflowIcon
        type="info"
        className="h-4 w-4 text-gray-500 shrink-0 mt-0.5"
      />
      <div className="flex flex-1 flex-col gap-4 min-w-0">
        <div className="flex flex-col gap-2">
          <p className="tf-header-sm-dark">
            {topicCount} topic{topicCount !== 1 ? "s" : ""}{" "}
            {pluralize("was", topicCount)}n't discussed last meeting.
          </p>
          <div className="flex flex-col gap-2">
            {undiscussedTopics.map(
              (topic) =>
                topic && (
                  <div key={topic.id} className="flex gap-2 items-center">
                    <img
                      src={getAssetUrl(undiscussedTopicSvg)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm text-gray-800">{topic.title}</span>
                  </div>
                ),
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button
            theme={buttonTheme.primary}
            disabled={isLoading}
            onClick={handleAddAll}
            size={ButtonSize.medium}
          >
            Add all to meeting
          </Button>
          <Button
            theme={buttonTheme.default}
            disabled={isLoading}
            onClick={handleMarkAllDiscussed}
            size={ButtonSize.medium}
          >
            Mark all as discussed
          </Button>
          {meeting.previousMeeting && (
            <Button
              theme={buttonTheme.default}
              to={`/meeting/${meeting.meetingGroupId}/${meeting.previousMeeting.id}`}
              size={ButtonSize.medium}
            >
              View last{" "}
              {meeting.meetingGroup?.isFormalOneonone
                ? label("1-on-1")
                : "meeting"}
            </Button>
          )}
        </div>
      </div>
      <button
        className="flex items-center p-1 rounded-md text-gray-400 hover:text-gray-600 shrink-0"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default UndiscussedTopicsBanner;
